import * as React from 'react'

import { F0Dialog, F0Select, createDataSourceDefinition } from '@factorialco/f0-react'
import {
  CardSelectableContainer,
  CardSelectableItem,
} from '@factorialco/f0-react/dist/experimental'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SchemaDataFieldsDocument } from 'generated/resources/graphql'

// --- Types ---

export interface UpdaterLegalEntityInfo {
  id: string
  legalName: string
}

export type UpdaterLeModeResult =
  | { mode: 'single'; legalEntityId: string }
  | { mode: 'multi'; selectedFields: string[] }

interface UpdaterLeModeModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (result: UpdaterLeModeResult) => void
  legalEntities: UpdaterLegalEntityInfo[]
  companyId: string
  importType: string
}

type LeMode = 'single' | 'multi'

// --- LE record for F0Select ---

type LeRecord = {
  id: string
  name: string
}

// --- Component ---

export const UpdaterLeModeModal: React.FC<UpdaterLeModeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  legalEntities,
  companyId,
  importType,
}) => {
  const [mode, setMode] = React.useState<LeMode>('single')
  const [selectedLeId, setSelectedLeId] = React.useState<string | undefined>(legalEntities[0]?.id)

  // Query schema fields to determine leDependant fields
  const metadata = React.useMemo(() => {
    const allLeIds = legalEntities.map((le) => le.id)
    return [{ key: 'legal_entity_ids', type: 'integer' as const, values: allLeIds }]
  }, [legalEntities])

  const {
    data: schemaData,
    loading: schemaLoading,
    error: schemaError,
  } = useQuery(SchemaDataFieldsDocument, {
    variables: {
      companyId,
      importType,
      metadata,
    },
    fetchPolicy: 'network-only',
    skip: !isOpen,
  })

  // Compute non-leDependant field keys for multi-LE mode
  const nonLeDependantFields = React.useMemo(() => {
    if (!schemaData?.universalImporter.schemaAdapters) return []
    return schemaData.universalImporter.schemaAdapters
      .filter((field) => !field.leDependant && !field.hidden)
      .map((field) => field.key)
  }, [schemaData])

  // Also include hidden fields (they're auto-included regardless)
  const hiddenFields = React.useMemo(() => {
    if (!schemaData?.universalImporter.schemaAdapters) return []
    return schemaData.universalImporter.schemaAdapters
      .filter((field) => field.hidden)
      .map((field) => field.key)
  }, [schemaData])

  const leRecords = React.useMemo<LeRecord[]>(
    () => legalEntities.map((le) => ({ id: le.id, name: le.legalName })),
    [legalEntities]
  )

  const leSource = React.useMemo(
    () =>
      createDataSourceDefinition({
        dataAdapter: {
          fetchData: ({ search }) => {
            const filtered = search
              ? leRecords.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
              : leRecords
            return { records: filtered }
          },
        },
      }),
    [leRecords]
  )

  const handleConfirm = () => {
    if (mode === 'single') {
      if (!selectedLeId) return
      onConfirm({ mode: 'single', legalEntityId: selectedLeId })
    } else {
      onConfirm({
        mode: 'multi',
        selectedFields: [...new Set([...nonLeDependantFields, ...hiddenFields])],
      })
    }
  }

  const cardItems: CardSelectableItem<LeMode>[] = React.useMemo(
    () => [
      {
        value: 'single' as const,
        title: i18n.t('universal_importer.updater_le_mode_modal.single_le_title'),
        description: i18n.t('universal_importer.updater_le_mode_modal.single_le_description'),
        selectedContent: (
          <F0Select
            label={i18n.t('universal_importer.updater_le_mode_modal.legal_entity_label')}
            required
            source={leSource}
            mapOptions={(item: LeRecord) => ({
              value: item.id,
              label: item.name,
            })}
            value={selectedLeId}
            onChange={(value: string | null) => setSelectedLeId(value ?? undefined)}
          />
        ),
      },
      {
        value: 'multi' as const,
        title: i18n.t('universal_importer.updater_le_mode_modal.multi_le_title'),
        description: i18n.t('universal_importer.updater_le_mode_modal.multi_le_description'),
      },
    ],
    [leSource, selectedLeId]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('universal_importer.updater_le_mode_modal.title')}
      description={i18n.t('universal_importer.updater_le_mode_modal.description')}
      primaryAction={{
        label: i18n.t('universal_importer.updater_le_mode_modal.confirm_button'),
        onClick: handleConfirm,
        disabled:
          (mode === 'single' && !selectedLeId) ||
          (mode === 'multi' && (!!schemaError || schemaLoading)),
        loading: mode === 'multi' && schemaLoading,
      }}
      secondaryAction={{
        label: i18n.t('universal_importer.updater_le_mode_modal.cancel_button'),
        onClick: onClose,
      }}
    >
      <CardSelectableContainer<LeMode>
        items={cardItems}
        value={mode}
        onChange={(value) => {
          if (value) setMode(value)
        }}
        label={i18n.t('universal_importer.updater_le_mode_modal.select_mode_label')}
      />
    </F0Dialog>
  )
}
